import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  FileText, ArrowLeft, Edit, Archive, ArchiveRestore, Trash2, Calendar, 
  User, Users, Package, Wrench, DollarSign, FileCheck, Camera, 
  Building, Phone, Mail, AlertCircle, CheckCircle, Clock, Banknote,
  Image, X, Star, AlertTriangle, Printer, Bookmark
} from 'lucide-react';
import Header from './Header';
import EquipmentPhotoUpload from './EquipmentPhotoUpload';
import BrandBadge from './BrandBadge';
import BlacklistAlert, { BlacklistBadge, BlacklistWarningCard } from './BlacklistAlert';
import BlacklistIncidentForm from './BlacklistIncidentForm';
import { useAuth } from '../contexts/AuthContext';
import { 
  deleteOrder, 
  archiveOrder,
  unarchiveOrder,
  toggleOrderBookmark,
  formatDate, 
  getOrderTypeDisplayText, 
  getStatusColor,
  confirmArchiveOrder
} from '../lib/orderService';
import { deleteEquipmentPhoto as deletePhotoFromStorage } from '../lib/photoStorageService';
import { checkClientBlacklistStatus } from '../lib/blacklistService';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole } = useAuth();
  
  // Check if user came from dashboard and parse preserved search state
  const urlParams = new URLSearchParams(location.search);
  const fromDashboard = urlParams.get('from') === 'dashboard';
  
  // Build back URL with preserved search state
  const buildBackUrl = () => {
    if (fromDashboard) {
      return '/';
    }
    
    const backParams = new URLSearchParams();
    const search = urlParams.get('search');
    const type = urlParams.get('type');
    const bookmarked = urlParams.get('bookmarked');
    const hideArchived = urlParams.get('hideArchived');
    const page = urlParams.get('page');
    
    if (search) backParams.set('search', search);
    if (type) backParams.set('type', type);
    if (bookmarked) backParams.set('bookmarked', bookmarked);
    if (hideArchived) backParams.set('hideArchived', hideArchived);
    if (page) backParams.set('page', page);
    
    const queryString = backParams.toString();
    return `/orders${queryString ? `?${queryString}` : ''}`;
  };
  const [order, setOrder] = useState(null);
  const [brands, setBrands] = useState([]);
  const [client, setClient] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showBlacklistForm, setShowBlacklistForm] = useState(false);
  const [photosSaving, setPhotosSaving] = useState(new Set());
  const [photosSaved, setPhotosSaved] = useState(new Set());
  const [photoSaveCompletion, setPhotoSaveCompletion] = useState(new Map()); // Track save completion per equipment
  const [photosHaveChanges, setPhotosHaveChanges] = useState(new Map());
  const [photosInEditMode, setPhotosInEditMode] = useState(new Map()); // Track unsaved changes per equipment
  const [photoCancelRefs, setPhotoCancelRefs] = useState(new Map()); // Cancel function refs per equipment
  const [specs, setSpecs] = useState([]); // Specifications with priority data
  const [showImporteTooltip, setShowImporteTooltip] = useState(false); // Mobile tooltip state

  useEffect(() => {
    fetchOrderData();
    fetchBrands();
  }, [id]);

  // Hide tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showImporteTooltip && !event.target.closest('.importe-tooltip-container')) {
        setShowImporteTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showImporteTooltip]);


  const fetchOrderData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch order details
      const orderDoc = await getDoc(doc(db, 'orders', id));
      if (!orderDoc.exists()) {
        setError('Orden no encontrada');
        setLoading(false);
        return;
      }

      const orderData = { id: orderDoc.id, ...orderDoc.data() };
      setOrder(orderData);

      // Fetch client information if order has client
      if (orderData.cliente) {
        try {
          const clientDoc = await getDoc(doc(db, 'clients', orderData.cliente));
          if (clientDoc.exists()) {
            const clientData = { id: clientDoc.id, ...clientDoc.data() };
            
            // Load blacklist status for client
            try {
              const blacklistStatus = await checkClientBlacklistStatus(orderData.cliente);
              clientData.isBlacklisted = blacklistStatus.isBlacklisted;
              clientData.activeIncidentsCount = blacklistStatus.activeIncidentsCount;
              clientData.mostRecentIncidentMotivo = blacklistStatus.mostRecentIncidentMotivo;
              clientData.mostRecentIncident = blacklistStatus.mostRecentIncident;
            } catch (error) {
              console.error('Error checking blacklist status:', error);
            }
            
            setClient(clientData);
          }
        } catch (error) {
          console.error('Error fetching client:', error);
        }
      }

      // Fetch technician information
      if (orderData.tecnicos && orderData.tecnicos.length > 0) {
        try {
          const technicianPromises = orderData.tecnicos.map(async (techId) => {
            try {
              const techDoc = await getDoc(doc(db, 'technicians', techId));
              if (techDoc.exists()) {
                return { id: techDoc.id, ...techDoc.data() };
              }
              return null;
            } catch (error) {
              console.error('Error fetching technician:', error);
              return null;
            }
          });
          
          const technicianResults = await Promise.all(technicianPromises);
          setTechnicians(technicianResults.filter(tech => tech !== null));
        } catch (error) {
          console.error('Error fetching technicians:', error);
        }
      }

      // Fetch equipment information (both catalog and exempt)
      if (orderData.equipos && orderData.equipos.length > 0) {
        try {
          // Get all equipment data once for efficient lookup
          let allEquipmentData = null;
          const catalogEquipmentIds = orderData.equipos
            .filter(eq => eq.catalogId)
            .map(eq => eq.catalogId);
          
          if (catalogEquipmentIds.length > 0) {
            try {
              // Use equipmentService to get properly populated equipment data
              const { getAllEquipment } = await import('../lib/equipmentService');
              allEquipmentData = await getAllEquipment(true); // Include archived
            } catch (error) {
              console.error('Error fetching all equipment data:', error);
            }
          }
          
          const equipmentPromises = orderData.equipos.map(async (equipo) => {
            // If it has catalogId, it's catalog equipment
            if (equipo.catalogId) {
              try {
                // First try to use the pre-fetched equipment data
                if (allEquipmentData) {
                  const equipData = allEquipmentData.find(eq => eq.id === equipo.catalogId);
                  if (equipData) {
                    return {
                      ...equipo,
                      catalogData: equipData,
                      type: 'catalog'
                    };
                  }
                }
                
                // Fallback to direct Firestore access if equipmentService data not available
                const equipDoc = await getDoc(doc(db, 'equipment_catalog', equipo.catalogId));
                if (equipDoc.exists()) {
                  return {
                    ...equipo,
                    catalogData: equipDoc.data(),
                    type: 'catalog'
                  };
                }
              } catch (error) {
                console.error('Error fetching catalog equipment:', error);
              }
            }
            
            // Return as exempt equipment if no catalog data
            return {
              ...equipo,
              type: 'exempt'
            };
          });
          
          const equipmentResults = await Promise.all(equipmentPromises);
          
          // Add photos data to equipment if it exists in the order
          const equipmentWithPhotos = equipmentResults.map((equipmentItem, index) => ({
            ...equipmentItem,
            photos: (orderData.equipos[index]?.photos || []).map(photo => ({
              ...photo,
              // Ensure we have both id and photoId for deletion
              id: photo.id || photo.photoId,
              photoId: photo.photoId || photo.id,
              filename: photo.fileName || photo.filename,
              fileName: photo.fileName || photo.filename
            }))
          }));
          
          setEquipment(equipmentWithPhotos);
        } catch (error) {
          console.error('Error fetching equipment:', error);
        }
      }

      // Fetch specifications with their priorities for proper ordering
      try {
        const specsQuery = query(collection(db, 'specs'), orderBy('prioridad'));
        const specsSnapshot = await getDocs(specsQuery);
        const specsList = specsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSpecs(specsList);
      } catch (error) {
        console.log('Could not fetch specs data, using default order');
        setSpecs([]);
      }

    } catch (error) {
      console.error('Error fetching order data:', error);
      setError('Error al cargar la información de la orden');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const brandsQuery = query(collection(db, 'marcas'));
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      console.log('Brands collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  // Helper function to get brand object by ID or name
  const getBrandByNameOrId = (identifier) => {
    if (!identifier || brands.length === 0) {
      return { nombre: identifier || 'Sin marca', color: '#6B7280' };
    }
    
    // Try to find by ID first (new format)
    let brand = brands.find(b => b.id === identifier);
    
    // If not found, try by name (backward compatibility)
    if (!brand) {
      brand = brands.find(b => b.nombre === identifier);
    }
    
    // Return found brand or fallback
    return brand || { nombre: identifier, color: '#6B7280' };
  };

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const canManageBlacklist = () => {
    return userRole === 'admin';
  };

  const handleAddToBlacklist = () => {
    setShowBlacklistForm(true);
  };

  const handleBlacklistFormSuccess = (message) => {
    setSuccess(message);
    setShowBlacklistForm(false);
    
    // Refresh client data to update blacklist status
    fetchOrderData();
    
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleBlacklistFormClose = () => {
    setShowBlacklistForm(false);
  };

  const handleBlacklistWarningClick = (incidentId, orderId) => {
    // Navigate to blacklist incident detail with back navigation support
    const params = new URLSearchParams();
    params.set('from', 'order');
    params.set('orderId', orderId);
    navigate(`/clients/blacklist/${incidentId}?${params.toString()}`);
  };

  const handleGenerateReport = () => {
    // Open order report in new tab
    const reportUrl = `/orders/${order.id}/report`;
    window.open(reportUrl, '_blank');
  };

  const getClientDisplayName = () => {
    if (!client) return 'Sin cliente';
    const principal = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
    return client.nombreEmpresa || principal?.nombre || 'Cliente';
  };

  const getPrincipalContact = (contactos) => {
    if (!contactos || contactos.length === 0) return null;
    return contactos.find(c => c.esPrincipal) || contactos[0];
  };

  const getOrderTypeColor = (tipo) => {
    const typeColors = {
      'Reparación': 'bg-blue-50 text-blue-700 border-blue-200',
      'Ensamble': 'bg-purple-50 text-purple-700 border-purple-200',
      'Garantía': 'bg-orange-50 text-orange-700 border-orange-200',
      'Fabricación': 'bg-gray-50 text-gray-700 border-gray-200'
    };
    
    return typeColors[tipo] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const handleBookmarkToggle = async () => {
    try {
      setActionLoading(true);
      const updatedOrder = await toggleOrderBookmark(order.id);
      setOrder(updatedOrder);
      setSuccess(`Orden ${updatedOrder.isBookmarked ? 'marcada' : 'desmarcada'} como favorita`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      setError('Error al cambiar marcador de orden');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      // Use shared validation and confirmation function
      const confirmed = await confirmArchiveOrder(order);
      if (!confirmed) {
        return;
      }

      setActionLoading(true);
      const updatedOrder = await archiveOrder(order.id);
      setOrder(updatedOrder);
      setSuccess(`Orden ${order.folio} ha sido archivada`);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error archiving order:', error);
      setError('Error al archivar la orden');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnarchive = async () => {
    try {
      if (!window.confirm(
        `¿Está seguro de desarchivar la orden ${order.folio}?\n\n` +
        `La orden volverá a la sección activa.`
      )) {
        return;
      }

      setActionLoading(true);
      const updatedOrder = await unarchiveOrder(order.id);
      setOrder(updatedOrder);
      setSuccess(`Orden ${order.folio} ha sido desarchivada`);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error unarchiving order:', error);
      setError('Error al desarchivar la orden');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente la orden ${order.folio}?\n\n` +
        `Esta acción no se puede deshacer.`
      )) {
        return;
      }

      setActionLoading(true);
      await deleteOrder(order.id);
      setSuccess(`Orden ${order.folio} ha sido eliminada permanentemente`);
      
      setTimeout(() => {
        navigate(fromDashboard ? '/' : '/orders');
      }, 2000);
      
    } catch (error) {
      console.error('Error deleting order:', error);
      setError('Error al eliminar la orden');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const hasFinancialInfo = (orderType) => {
    return ['Reparación', 'Ensamble', 'Garantía'].includes(orderType);
  };

  // Photo management functions
  const handlePhotosChange = (equipmentIndex, newPhotos) => {
    
    setEquipment(prevEquipment => {
      const updatedEquipment = [...prevEquipment];
      
      // Format photos to match Firestore structure
      const formattedPhotos = newPhotos.map((photo, index) => ({
        id: photo.photoId || photo.id,
        fileName: photo.filename || photo.fileName,
        downloadURL: photo.downloadURL,
        comment: photo.comment || '',
        order: photo.order || index + 1,
        uploadedAt: photo.uploadedAt || new Date().toISOString(),
        fileSize: photo.size || photo.fileSize || 0,
        // Store photoId for storage deletion
        photoId: photo.photoId || photo.id,
        // Preserve deletion flag for save operation
        markedForDeletion: photo.markedForDeletion || false
      }));

      updatedEquipment[equipmentIndex] = {
        ...updatedEquipment[equipmentIndex],
        photos: formattedPhotos
      };

      return updatedEquipment;
    });
  };

  const saveEquipmentPhotos = async (equipmentIndex) => {
    if (!order || !equipment[equipmentIndex]) return;

    try {
      setPhotosSaving(prev => new Set([...prev, equipmentIndex]));
      setError('');

      // Get current equipment data
      const currentEquipment = equipment[equipmentIndex];
      const equipmentId = currentEquipment?.catalogId || `equipment-${equipmentIndex}`;
      
      // Process photos marked for deletion
      const allPhotos = currentEquipment.photos || [];
      const photosToDelete = allPhotos.filter(photo => photo.markedForDeletion);
      const photosToKeep = allPhotos.filter(photo => !photo.markedForDeletion);
      
      
      // Delete marked photos from Firebase Storage
      for (const photo of photosToDelete) {
        if (photo.photoId && order?.id) {
          // Skip deletion attempt if we know the file doesn't exist in storage
          // This can happen with photos that were deleted outside the app or legacy data
          if (!photo.downloadURL || photo.downloadURL.includes('placeholder')) {
            continue;
          }
          
          try {
            // Use photoId as the storage filename
            await deletePhotoFromStorage(order.id, equipmentId, photo.photoId);
          } catch (deleteError) {
            // If deletion fails (file not found, etc.), continue with the save
            // The photo will be removed from the database even if storage deletion fails
            console.warn(`Could not delete photo ${photo.photoId} from storage (may already be deleted):`, deleteError.message);
          }
        }
      }
      
      const updatedEquipos = [...order.equipos];
      
      // Find the matching equipment in the order
      const equipmentItem = updatedEquipos[equipmentIndex];
      
      if (equipmentItem) {
        // Update the photos in the equipment item (only keep non-deleted photos)
        equipmentItem.photos = photosToKeep;
        
        // Update the order in Firestore
        await updateDoc(doc(db, 'orders', order.id), {
          equipos: updatedEquipos,
          updatedAt: new Date()
        });

        // Update local equipment state to remove deleted photos
        setEquipment(prev => prev.map((eq, idx) => 
          idx === equipmentIndex 
            ? { ...eq, photos: photosToKeep }
            : eq
        ));

        // Update local order state
        setOrder(prevOrder => ({
          ...prevOrder,
          equipos: updatedEquipos
        }));

        const deleteMessage = photosToDelete.length > 0 
          ? ` (${photosToDelete.length} foto${photosToDelete.length > 1 ? 's' : ''} eliminada${photosToDelete.length > 1 ? 's' : ''})`
          : '';

        setSuccess(`Fotos guardadas exitosamente${deleteMessage}`);
        setTimeout(() => setSuccess(''), 3000);

        // Add equipment index to saved set for visual feedback
        setPhotosSaved(prev => new Set([...prev, equipmentIndex]));
        
        // Signal save completion to EquipmentPhotoUpload component
        setPhotoSaveCompletion(prev => new Map([...prev, [equipmentIndex, Date.now()]]));
        
        setTimeout(() => {
          setPhotosSaved(prev => {
            const newSet = new Set(prev);
            newSet.delete(equipmentIndex);
            return newSet;
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving equipment photos:', error);
      setError(`Error al guardar las fotos: ${error.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setPhotosSaving(prev => {
        const newSet = new Set(prev);
        newSet.delete(equipmentIndex);
        return newSet;
      });
    }
  };

  const deleteEquipmentPhoto = async (equipmentIndex, photoId, fileName) => {
    if (!canEdit()) return;

    try {
      setError('');
      
      // Get equipment ID for storage path
      const currentEquipment = equipment[equipmentIndex];
      const equipmentId = currentEquipment?.catalogId || `equipment-${equipmentIndex}`;
      
      // Delete from Firebase Storage if photoId/fileName exists
      if (photoId && order?.id) {
        try {
          // Use the fileName if available, otherwise use photoId as fallback
          const storageFileName = fileName || photoId;
          console.log('Attempting to delete photo from storage:', {
            orderId: order.id,
            equipmentId,
            storageFileName,
            originalPhotoId: photoId,
            originalFileName: fileName
          });
          
          await deletePhotoFromStorage(order.id, equipmentId, storageFileName);
          console.log('Successfully deleted photo from storage:', storageFileName);
        } catch (storageError) {
          console.error('Error deleting from storage:', storageError);
          // Continue with Firestore deletion even if storage deletion fails
          // This handles cases where the file might already be deleted from storage
        }
      } else {
        console.warn('Cannot delete from storage - missing data:', {
          photoId,
          orderId: order?.id,
          equipmentId
        });
      }
      
      // Remove photo from local state
      setEquipment(prevEquipment => {
        const updatedEquipment = [...prevEquipment];
        const currentEquipment = updatedEquipment[equipmentIndex];
        
        if (currentEquipment && currentEquipment.photos) {
          currentEquipment.photos = currentEquipment.photos.filter(photo => photo.id !== photoId);
        }
        
        return updatedEquipment;
      });

      // Auto-save after deletion
      setTimeout(() => saveEquipmentPhotos(equipmentIndex), 100);
      
      setSuccess('Foto eliminada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting equipment photo:', error);
      setError(`Error al eliminar la foto: ${error.message}`);
      setTimeout(() => setError(''), 5000);
    }
  };

  const updatePhotoOrder = async (equipmentIndex, reorderedPhotos) => {
    if (!canEdit()) return;

    try {
      setError('');
      
      // Update photo order in local state
      const photosWithOrder = reorderedPhotos.map((photo, index) => ({
        ...photo,
        order: index + 1
      }));

      setEquipment(prevEquipment => {
        const updatedEquipment = [...prevEquipment];
        updatedEquipment[equipmentIndex] = {
          ...updatedEquipment[equipmentIndex],
          photos: photosWithOrder
        };
        return updatedEquipment;
      });

      // Auto-save after reordering
      setTimeout(() => saveEquipmentPhotos(equipmentIndex), 100);
    } catch (error) {
      console.error('Error updating photo order:', error);
      setError(`Error al reordenar las fotos: ${error.message}`);
      setTimeout(() => setError(''), 5000);
    }
  };


  // Handle photo changes state
  const handlePhotosHaveChanges = (equipmentIndex, hasChanges) => {
    setPhotosHaveChanges(prev => new Map(prev.set(equipmentIndex, hasChanges)));
  };

  // Handle photo edit mode changes
  const handlePhotosEditModeChange = (equipmentIndex, isEditMode) => {
    setPhotosInEditMode(prev => new Map(prev.set(equipmentIndex, isEditMode)));
  };

  // Handle photo cancel
  const handlePhotosCancel = async (equipmentIndex) => {
    const cancelRef = photoCancelRefs.get(equipmentIndex);
    
    if (cancelRef && cancelRef.current && typeof cancelRef.current === 'function') {
      await cancelRef.current();
      // Reset change tracking
      setPhotosHaveChanges(prev => new Map(prev.set(equipmentIndex, false)));
      setPhotosSaved(prev => {
        const newSet = new Set(prev);
        newSet.delete(equipmentIndex);
        return newSet;
      });
    }
  };

  // Initialize cancel ref for equipment
  const initPhotoCancelRef = (equipmentIndex) => {
    if (!photoCancelRefs.has(equipmentIndex)) {
      const ref = { current: null };
      setPhotoCancelRefs(prev => new Map(prev.set(equipmentIndex, ref)));
      return ref;
    }
    return photoCancelRefs.get(equipmentIndex);
  };

  // Helper function to get specifications ordered by priority
  const getOrderedSpecifications = (especificaciones) => {
    if (!especificaciones || Object.keys(especificaciones).length === 0) return [];
    
    // Create a map of spec names to their priority for quick lookup
    const specPriorityMap = {};
    specs.forEach(spec => {
      specPriorityMap[spec.nombre] = spec.prioridad || 999; // Default high priority for specs without priority
    });
    
    // Convert equipment specs to array and sort by priority
    const equipmentSpecs = Object.entries(especificaciones);
    
    return equipmentSpecs.sort((a, b) => {
      const priorityA = specPriorityMap[a[0]] || 999;
      const priorityB = specPriorityMap[b[0]] || 999;
      return priorityA - priorityB; // Lower priority number = higher priority (displayed first)
    });
  };

  // Helper function to merge catalog specs with custom specs for equipment
  const getMergedSpecifications = (equipo) => {
    if (!equipo.catalogData || !equipo.catalogData.especificaciones) return {};
    
    const catalogSpecs = equipo.catalogData.especificaciones;
    const customSpecs = equipo.customSpecs || {};
    
    // Create merged specifications - custom values override catalog defaults
    const mergedSpecs = { ...catalogSpecs };
    
    // Override with custom values where they exist and are not empty
    Object.keys(customSpecs).forEach(specName => {
      if (customSpecs[specName] && customSpecs[specName].trim() !== '') {
        mergedSpecs[specName] = customSpecs[specName];
      }
    });
    
    return mergedSpecs;
  };

  // Helper function to check if a spec value is custom (different from catalog default)
  const isCustomSpec = (equipo, specName, specValue) => {
    if (!equipo.catalogData || !equipo.catalogData.especificaciones) return false;
    
    const catalogValue = equipo.catalogData.especificaciones[specName];
    const customSpecs = equipo.customSpecs || {};
    
    // Check if this spec has a custom value that's different from catalog
    return customSpecs[specName] && 
           customSpecs[specName].trim() !== '' && 
           customSpecs[specName] !== catalogValue;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando información de la orden...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
          <div className="mt-4">
            <Link 
              to={buildBackUrl()}
              className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{fromDashboard ? "Volver al Dashboard" : "Volver a Órdenes"}</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto p-6">
        {/* Navigation */}
        <div className="mb-6">
          <Link 
            to={buildBackUrl()}
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{fromDashboard ? "Volver al Dashboard" : "Volver a Órdenes"}</span>
          </Link>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-6">
            {success}
          </div>
        )}

        {/* Order Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                <div>
                  <div className="flex items-center space-x-3">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                      Orden {order.folio}
                    </h1>
                    {canEdit() ? (
                      <button
                        onClick={handleBookmarkToggle}
                        disabled={actionLoading}
                        className={`p-2 rounded transition-colors disabled:opacity-50 ${
                          order.isBookmarked
                            ? 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20'
                            : 'text-gray-400 dark:text-gray-500 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                        }`}
                        title={order.isBookmarked ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                      >
                        <Bookmark className={`h-6 w-6 ${order.isBookmarked ? 'fill-current' : ''}`} />
                      </button>
                    ) : (
                      // Read-only bookmark display for users without edit permissions
                      order.isBookmarked && (
                        <div 
                          className="p-2 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded"
                          title="Orden favorita (solo lectura)"
                        >
                          <Bookmark className="h-6 w-6 fill-current" />
                        </div>
                      )
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getOrderTypeColor(order.tipo_de_orden)}`}>
                      {getOrderTypeDisplayText(order.tipo_de_orden)}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status_orden)}`}>
                      {order.status_orden || 'Sin estado'}
                    </span>
                    {order.isArchived && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                        Archivada
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap justify-end gap-1 sm:flex-nowrap">
                {canEdit() && (
                  <Link
                    to={(() => {
                      const editParams = new URLSearchParams(urlParams);
                      editParams.set('from', fromDashboard ? 'dashboard' : 'detail');
                      return `/orders/${order.id}/edit?${editParams.toString()}`;
                    })()}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded disabled:opacity-50"
                    title="Editar orden"
                  >
                    <Edit className="h-5 w-5" />
                  </Link>
                )}

                {/* Report Button - Available to all users */}
                <button
                  onClick={handleGenerateReport}
                  disabled={actionLoading}
                  className="p-2 text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded disabled:opacity-50"
                  title="Generar reporte"
                >
                  <Printer className="h-5 w-5" />
                </button>

                {canEdit() && (
                  <>
                  
                  {order.isArchived ? (
                    <button
                      onClick={handleUnarchive}
                      disabled={actionLoading}
                      className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded disabled:opacity-50"
                      title="Desarchivar orden"
                    >
                      <ArchiveRestore className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleArchive}
                      disabled={actionLoading}
                      className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded disabled:opacity-50"
                      title="Archivar orden"
                    >
                      <Archive className="h-5 w-5" />
                    </button>
                  )}
                  
                  {canManageBlacklist() && client && order.tipo_de_orden !== 'Fabricación' && (
                    <button
                      onClick={handleAddToBlacklist}
                      disabled={actionLoading}
                      className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                      title="Agregar cliente a lista negra"
                    >
                      <AlertTriangle className="h-5 w-5" />
                    </button>
                  )}
                  
                  {canDelete() && (
                    <button
                      onClick={handleDelete}
                      disabled={actionLoading}
                      className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                      title="Eliminar orden"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                  </>
                )}
              </div>
            </div>
            
            {/* Order Dates */}
            <div className="mt-6">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Fecha de creación:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(order.fecha)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Client Information Card */}
        {order.tipo_de_orden !== 'Fabricación' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-6">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información del Cliente</h3>
                  </div>
                  
                </div>
                
                {/* Blacklist Warning Card */}
                {client && (
                  <BlacklistWarningCard 
                    client={client} 
                    className="mb-4"
                    clickable={true}
                    orderId={order.id}
                    onClick={handleBlacklistWarningClick}
                  />
                )}
                
                {client ? (
                  <div className="space-y-3">
                    <Link
                      to={`/clients/${client.id}?from=order&orderId=${order.id}`}
                      className="block hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-md transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <BlacklistAlert client={client} size="sm" />
                        <h4 className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          {client.nombreEmpresa || (() => {
                            const principal = getPrincipalContact(client.contactos);
                            return principal?.nombre || 'Cliente sin nombre';
                          })()}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{client.tipoCliente === 'empresa' ? 'Empresa' : 'Particular'}</p>
                    </Link>
                    
                    {client.contactos && client.contactos.length > 0 && (
                      <div className="space-y-4">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Contactos</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {client.contactos.map((contact, index) => (
                            <div key={index} className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 relative">
                              {/* Main Contact Star */}
                              {contact.esPrincipal && (
                                <div className="absolute top-3 right-3">
                                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                                </div>
                              )}
                              
                              {/* Contact Header */}
                              <div className="flex items-center space-x-2 mb-3">
                                <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                <span className="font-medium text-gray-900 dark:text-white">{contact.nombre}</span>
                                {contact.esPrincipal && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Principal</span>
                                )}
                              </div>
                              
                              {/* Contact Details */}
                              <div className="space-y-2">
                                {contact.telefono && (
                                  <div className="flex items-center space-x-2">
                                    <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{contact.telefono}</span>
                                  </div>
                                )}
                                {contact.email && (
                                  <div className="flex items-center space-x-2">
                                    <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{contact.email}</span>
                                  </div>
                                )}
                                {contact.nota && (
                                  <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">Notas:</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">{contact.nota}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : order.cliente ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Cliente ID: {order.cliente} (No se pudo cargar la información)
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Sin cliente asignado
                  </div>
                )}
              </div>
            </div>
          )}

        {/* For Fabricación orders, show "No requiere cliente" */}
        {order.tipo_de_orden === 'Fabricación' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-6">
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información del Cliente</h3>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <AlertCircle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">No requiere cliente (Orden de Fabricación)</span>
                </div>
              </div>
            </div>
          )}

        {/* Equipment Details Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Package className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Equipos</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({equipment.length} {equipment.length === 1 ? 'equipo' : 'equipos'})
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <Image className="h-4 w-4" />
                <span>
                  {equipment.reduce((total, equipo) => total + (equipo.photos?.length || 0), 0)} fotos totales
                </span>
              </div>
            </div>
            
            {equipment.length > 0 ? (
              <div className="space-y-4">
                {equipment.map((equipo, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                            equipo.type === 'catalog' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {equipo.type === 'catalog' ? 'Catálogo' : 'Exento'}
                          </span>
                        </div>
                        
                        {equipo.type === 'catalog' && equipo.catalogData ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Left Column - Basic Equipment Info */}
                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {equipo.catalogData.tipo && `${equipo.catalogData.tipo} `}
                                <BrandBadge brand={getBrandByNameOrId(equipo.catalogData.marca)} size="md" className="mr-1" />
                                {equipo.catalogData.modelo}
                                {equipo.catalogData.numeroParte && ` (${equipo.catalogData.numeroParte})`}
                              </h4>
                              {equipo.series && (
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                  <span className="font-medium text-gray-600 dark:text-gray-400">Serie:</span> {equipo.series}
                                </div>
                              )}
                              
                              {equipo.notes && (
                                <div className="text-sm">
                                  <span className="font-medium text-gray-600 dark:text-gray-400">Notas:</span>
                                  <span className="text-gray-900 dark:text-white ml-1">{equipo.notes}</span>
                                </div>
                              )}
                            </div>

                            {/* Right Column - Specifications */}
                            <div>
                              {(() => {
                                const mergedSpecs = getMergedSpecifications(equipo);
                                if (Object.keys(mergedSpecs).length === 0) return null;
                                
                                return (
                                  <div>
                                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Especificaciones</h5>
                                    <ul className="text-sm text-gray-900 dark:text-white space-y-1">
                                      {getOrderedSpecifications(mergedSpecs).map(([specName, specValue]) => (
                                        <li key={specName} className="flex">
                                          <span className="mr-2">•</span>
                                          <span>
                                            <strong>{specName}:</strong> {specValue}
                                            {isCustomSpec(equipo, specName, specValue) && (
                                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                Personalizado
                                              </span>
                                            )}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {equipo.descripcion || 'Equipo sin descripción'}
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              {equipo.series && (
                                <div>
                                  <span className="font-medium text-gray-600 dark:text-gray-400">Serie:</span>
                                  <div className="text-gray-900 dark:text-white">{equipo.series}</div>
                                </div>
                              )}
                            </div>
                            {equipo.notes && (
                              <div className="text-sm">
                                <span className="font-medium text-gray-600 dark:text-gray-400">Notas:</span>
                                <span className="text-gray-900 dark:text-white ml-1">{equipo.notes}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Photo Section */}
                    <div className="mt-4 border-t pt-4">
                        <div className="mb-4">
                          <EquipmentPhotoUpload
                            orderId={order.id}
                            equipmentId={equipo.catalogId || `equipment-${index}`}
                            existingPhotos={equipo.photos || []}
                            onPhotosChange={(newPhotos) => handlePhotosChange(index, newPhotos)}
                            canEdit={canEdit()}
                            onSaveComplete={photoSaveCompletion.get(index)}
                            onHasChanges={(hasChanges) => handlePhotosHaveChanges(index, hasChanges)}
                            onCancelRef={initPhotoCancelRef(index)}
                            onSave={() => saveEquipmentPhotos(index)}
                            onEditModeChange={(isEditMode) => handlePhotosEditModeChange(index, isEditMode)}
                          />
                        </div>
                        
                        {/* Save/Cancel Photos Buttons - Only show when there are changes and NOT in edit mode */}
                        {canEdit() && photosHaveChanges.get(index) && !photosInEditMode.get(index) && (
                          <div className="flex flex-col items-end space-y-2">
                            <div className="flex space-x-2">
                              {/* Cancel Button */}
                              <button
                                onClick={() => handlePhotosCancel(index)}
                                disabled={photosSaving.has(index)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                title="Cancelar cambios y eliminar fotos subidas"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Cancelar
                              </button>
                              
                              {/* Save Button */}
                              <button
                                onClick={() => saveEquipmentPhotos(index)}
                                disabled={photosSaving.has(index) || photosSaved.has(index)}
                                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed transition-colors duration-200 ${
                                  photosSaved.has(index)
                                    ? 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500'
                                    : photosSaving.has(index)
                                    ? 'text-white bg-purple-400 opacity-75'
                                    : 'text-white bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                                }`}
                              >
                                {photosSaving.has(index) ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Guardando...
                                  </>
                                ) : photosSaved.has(index) ? (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    ¡Guardado!
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Guardar
                                  </>
                                )}
                              </button>
                            </div>
                            
                            {/* Inline success message */}
                            {photosSaved.has(index) && (
                              <div className="text-xs text-green-600 dark:text-green-400 font-medium animate-pulse">
                                Fotos guardadas exitosamente
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Sin equipos registrados
              </div>
            )}
          </div>
        </div>

        {/* Technician Assignment Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-6">
          <div className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Wrench className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Técnicos Asignados</h3>
            </div>
            
            {technicians.length > 0 ? (
              <div className="space-y-3">
                {technicians.map((tech) => (
                  <div key={tech.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{tech.nombre}</div>
                      {tech.telefono && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">{tech.telefono}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Sin técnicos asignados
              </div>
            )}
          </div>
        </div>

        {/* Service Details Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-6">
          <div className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileCheck className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Detalles del Servicio</h3>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Observaciones y Detalles
                </label>
                <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-md min-h-[80px]">
                  {order.observaciones_detalle || 'No especificado'}
                </div>
              </div>
              
              {['Reparación', 'Garantía'].includes(order.tipo_de_orden) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Comentarios del Cliente
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-md min-h-[60px]">
                      {order.comentarios_cliente || 'No especificado'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Resultados de Revisión
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-md min-h-[60px]">
                      {order.resultados_revision || 'No especificado'}
                    </div>
                  </div>
                </div>
              )}
              
              {order.tipo_de_orden === 'Garantía' && (
                <div>
                  {/* Warranty Information Header */}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Información sobre Garantía
                  </h3>
                  
                  {/* RMA and Veredicto in 2-column grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {order.rma && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Información de RMA
                        </label>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {order.rma}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Folio de Compra
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {order.folio_compra || 'No especificado'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha de Compra del Equipo
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {order.fecha_compra_equipo ? formatDate(order.fecha_compra_equipo) : 'No especificado'}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Veredicto
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {order.veredicto || 'No especificado'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Detalle del Veredicto - Full width */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Detalle del Veredicto
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-md min-h-[60px]">
                      {order.detalle_veredicto || 'No especificado'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Financial Information Card */}
        {hasFinancialInfo(order.tipo_de_orden) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mt-6">
            <div className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Datos Administrativos</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* First Column: Cotización, Importe, Estado de Autorización, Fecha de Autorización */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Folio de Cotización
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {order.folio_cotizacion || 'No especificado'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Importe
                    </label>
                    <div className="relative importe-tooltip-container">
                      <div 
                        className="text-sm text-gray-900 dark:text-white cursor-help inline-block"
                        title={order.importe ? `Antes de IVA: $${(order.importe / 1.16).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} MXN` : undefined}
                        onClick={() => {
                          if (order.importe) {
                            setShowImporteTooltip(!showImporteTooltip);
                          }
                        }}
                        onTouchStart={() => {
                          if (order.importe) {
                            setShowImporteTooltip(!showImporteTooltip);
                          }
                        }}
                      >
                        {order.importe ? `$${order.importe.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN` : 'No especificado'}
                      </div>
                      
                      {/* Mobile/Touch Tooltip */}
                      {showImporteTooltip && order.importe && (
                        <div className="absolute z-10 top-full left-0 mt-1 p-2 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded shadow-lg whitespace-nowrap">
                          Antes de IVA: ${(order.importe / 1.16).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} MXN
                          <div className="absolute bottom-full left-2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-800 dark:border-b-gray-700"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Estado de Autorización
                    </label>
                    <div className="flex items-center space-x-2">
                      {order.autorizacion === 'Autorizado' ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-700 dark:text-green-400">Autorizado</span>
                        </>
                      ) : order.autorizacion === 'No autorizado' ? (
                        <>
                          <X className="h-4 w-4 text-red-500" />
                          <span className="text-sm text-red-700 dark:text-red-400">No autorizado</span>
                        </>
                      ) : order.autorizacion === 'No aplica' ? (
                        <>
                          <AlertCircle className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">No aplica</span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-700 dark:text-gray-300">—</span>
                      )}
                    </div>
                    {order.fecha_autorizacion && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatDate(order.fecha_autorizacion)}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Second Column: Folio de Orden de Compra, Folio de Factura, Folio de Salida ContPaq, Estado de Pago */}
                <div className="space-y-4">
                  {/* Show Folio de Orden de Compra for Ensamble orders or if it exists */}
                  {(order.tipo_de_orden === 'Ensamble' || order.folio_orden_compra) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Folio de Orden de Compra
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {order.folio_orden_compra || 'No especificado'}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Folio de Factura
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {order.folio_factura_contpaq || 'No especificado'}
                    </div>
                  </div>
                  
                  {/* Show Folio de Salida ContPaq for Ensamble orders or if it exists */}
                  {(order.tipo_de_orden === 'Ensamble' || order.folio_salida_contpaq) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Folio de Salida ContPaq
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {order.folio_salida_contpaq || 'No especificado'}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Estado de Pago
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {order.estatus_pago || '—'}
                    </div>
                  </div>
                </div>
                
                {/* Third Column: Fecha de Conclusión, Fecha de Entrega, Quien Recibe */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fecha de Conclusión del Servicio
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {order.fecha_conclusion_servicio ? formatDate(order.fecha_conclusion_servicio) : 'No especificado'}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fecha de Entrega
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {order.fecha_entrega_equipo ? formatDate(order.fecha_entrega_equipo) : 'No especificado'}
                    </div>
                  </div>
                  
                  {order.quien_recibe_equipo && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Quien Recibe
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {order.quien_recibe_equipo}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Blacklist Incident Form Modal */}
      <BlacklistIncidentForm
        isOpen={showBlacklistForm}
        onClose={handleBlacklistFormClose}
        onSuccess={handleBlacklistFormSuccess}
        clientId={client?.id}
        clientName={getClientDisplayName()}
        preselectedOrderId={order?.folio}
      />
    </div>
  );
};

export default OrderDetail;